import React, { useMemo, useState, useRef, useCallback, useContext } from 'react';
import { Box, Typography, Chip, Paper, Button, useTheme, useMediaQuery } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip as ChartTooltip,
  Filler,
} from 'chart.js';
import { RadialBarChart, RadialBar, PolarAngleAxis } from 'recharts';
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { downloadCspmReport, clearAllCspmMessages } from "../../../store/cspm/cspmSlice";
import { useSecurityScoreData } from "../../Dashboard/SecurityScore/useSecurityScoreData";
import { useAccessibleUserAccounts } from "../../../components/Common/HelperComponents/CustomHooks";
import { useOrganizations } from "../../../components/Common/HelperComponents/GetDataFromStore";
import CloudProviderMark from "../../../components/Common/CloudProviderMark";
import { CloudProviderContext, getAccountCloudType } from "../../../CloudProviderContext";
import { ACCOUNT_DETAIL_FONT_SX } from "../../../components/AccountDetailPolicy/accountDetailTypography";

const SEVERITY_COLORS = {
  CRITICAL: '#d32f2f',
  HIGH: '#f44336',
  MEDIUM: '#fbc02d',
  LOW: '#4caf50',
  INFORMATIONAL: '#1976d2',
};
const SEVERITY_BG_COLORS = {
  CRITICAL: '#ffebee',
  HIGH: '#ffebee',
  MEDIUM: '#fff9c4',
  LOW: '#e8f5e9',
  INFORMATIONAL: '#e3f2fd',
};
const SEVERITY_LABELS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL'];

/** Matches Comparative Scan / Configuration content cards. */
const CONFIG_CARD_SX = {
  backgroundColor: "#f3f4f6",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
};

/** White inner panels used inside Configuration-style cards. */
const INNER_PANEL_SX = {
  backgroundColor: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
};

const SECTION_TITLE_SX = {
  color: "#111827",
  fontSize: "0.875rem",
  fontWeight: 700,
  letterSpacing: "0.02em",
};

// Register Chart.js components
ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  ChartTooltip,
  Filler
);

const TREND_COLOR = "#1976d2";
const SUBTITLE_COLOR = "#6b7a90";

// Add percentage formatting helper
function formatPercent(val) {
  if (val == null || isNaN(val)) return '';
  const num = Number(val);
  if (Number.isInteger(num)) return `${num}%`;
  const fixed = num.toFixed(2);
  if (fixed.endsWith('00')) return `${num.toFixed(0)}%`;
  if (fixed.endsWith('0')) return `${num.toFixed(1)}%`;
  return `${fixed}%`;
}


const CSPMHeader = ({ scanObj, trendScore, isLoading = false }) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const {
    isDownloadingCspmReport,
    downloadCspmReportMessage,
    downloadCspmReportError,
  } = useSelector((state) => state.cspm);

  // Organization data hooks — only accCodes for the active cloud toggle (AWS or Azure)
  const { selectedProvider } = useContext(CloudProviderContext);
  const accessibleAccounts = useAccessibleUserAccounts();
  const organizations = useOrganizations();
  const accCodes = useMemo(
    () =>
      accessibleAccounts
        .filter((account) => getAccountCloudType(account) === selectedProvider)
        .map((account) => account.accountCode),
    [accessibleAccounts, selectedProvider]
  );
  const orgCode = organizations?.[0]?.orgcode;
  const { data: orgData, loading: orgLoading } = useSecurityScoreData(accCodes, orgCode);

  // Account view metrics
  const accountMetrics = useMemo(() => {
    return scanObj?.cspmMetricResult || {
      executedChecks: 0,
      passedChecks: 0,
      failedChecks: 0,
      percentChecks: 0,
      manual: 0,
      uniqueResources: 0,
      failedResources: 0,
      passedResources: 0,
    };
  }, [scanObj]);

  // Organization view metrics - transform Dashboard API data
  const orgMetrics = useMemo(() => {
    if (!orgData) {
      return {
        executedChecks: 0,
        passedChecks: 0,
        failedChecks: 0,
        percentChecks: 0,
        uniqueResources: 0,
        failedResources: 0,
        passedResources: 0,
        severityData: {
          CRITICAL: 0,
          HIGH: 0,
          MEDIUM: 0,
          LOW: 0,
          INFORMATIONAL: 0,
        },
      };
    }

    const totalFailed = 
      (orgData.securityPostCriticalTotal || 0) +
      (orgData.securityPostHighTotal || 0) +
      (orgData.securityPostModerateTotal || 0) +
      (orgData.securityPostLowTotal || 0);
    
    const totalPassed = (orgData.resourceTotalCount || 0) - (orgData.resourceFailedCount || 0);
    const totalChecks = totalFailed + totalPassed;

    return {
      executedChecks: totalChecks,
      passedChecks: totalPassed,
      failedChecks: totalFailed,
      percentChecks: parseFloat(orgData.securityPostSecPercent || 0),
      uniqueResources: orgData.resourceTotalCount || 0,
      failedResources: orgData.resourceFailedCount || 0,
      passedResources: totalPassed,
      severityData: {
        CRITICAL: orgData.securityPostCriticalTotal || 0,
        HIGH: orgData.securityPostHighTotal || 0,
        MEDIUM: orgData.securityPostModerateTotal || 0,
        LOW: orgData.securityPostLowTotal || 0,
        INFORMATIONAL: 0,
      },
    };
  }, [orgData]);

  // Trend chart data (last 30 days) - Format for Chart.js
  const trendChartData = useMemo(() => {
    if (!trendScore) return { labels: [], data: [] };
    const entries = Object.entries(trendScore).sort(
      ([a], [b]) => new Date(a) - new Date(b)
    );
    const last30 = entries.slice(-30);
    return {
      labels: last30.map(([date]) => {
        const d = new Date(date);
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${mm}-${dd}`;
      }),
      data: last30.map(([, value]) => parseFloat(value) || 0),
    };
  }, [trendScore]);

  const chartData = {
    labels: trendChartData.labels,
    datasets: [
      {
        label: "Security Score",
        data: trendChartData.data,
        fill: true,
        borderColor: TREND_COLOR,
        backgroundColor: "rgba(25, 118, 210, 0.10)",
        pointRadius: 2,
        pointBackgroundColor: TREND_COLOR,
        tension: 0.35,
        borderWidth: 2,
      },
    ],
  };

  const chartRef = useRef();

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: "white",
        titleColor: "#1a2233",
        bodyColor: "#1a2233",
        borderColor: "#e3eafc",
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        callbacks: {
          label: function(context) {
            return `Score: ${formatPercent(context.parsed.y)}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: { color: "#f0f0f0", drawBorder: false },
        ticks: {
          color: SUBTITLE_COLOR,
          font: { size: 10, weight: 500 },
          maxTicksLimit: 6,
          padding: 4,
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        display: true,
        grid: { display: false },
        ticks: { display: false },
        min: 0,
        max: 100,
      },
    },
    elements: {
      line: { borderWidth: 2 },
      point: { radius: 2 },
    },
  }), []);

  // Calculate severity data for account view
  const accountSeverityData = useMemo(() => {
    const result = {};
    if (scanObj?.auditChecks?.Checks) {
      for (const sev of SEVERITY_LABELS) {
        result[sev] = 0;
      }
      scanObj.auditChecks.Checks.forEach(check => {
        const sev = (check.Severity || '').toUpperCase();
        if (SEVERITY_LABELS.includes(sev) && check.Status === 'FAILED') {
          result[sev] += 1;
        }
      });
    }
    return result;
  }, [scanObj]);

  // Organization severity data
  const orgSeverityData = orgMetrics.severityData || {};

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Download file when URL is received
  React.useEffect(() => {
    console.log("🚀 ~ React.useEffect ~ downloadCspmReportMessage:", downloadCspmReportMessage)
    if (downloadCspmReportMessage) {
      
      const link = document.createElement("a");
      link.href = downloadCspmReportMessage;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => {
        dispatch(clearAllCspmMessages());
      }, 500);
    }
  }, [downloadCspmReportMessage, dispatch]);

  // Show error snackbar
  React.useEffect(() => {
    if (downloadCspmReportError) {
      setSnackbar({
        open: true,
        message: downloadCspmReportError,
        severity: "error",
      });
      setTimeout(() => {
        dispatch(clearAllCspmMessages());
      }, 4000);
    }
  }, [downloadCspmReportError, dispatch]);

  const handleDownloadReport = () => {
    if (scanObj?.runId) {
      dispatch(downloadCspmReport({ runId: scanObj.runId }));
    }
  };

  // Calculate resources for both views
  const accountTotal = accountMetrics.uniqueResources || 0;
  const accountPassed = accountMetrics.passedResources || 0;
  const accountFailed = accountMetrics.failedResources || 0;
  const accountPassedPercent = accountTotal > 0 ? (accountPassed / accountTotal) * 100 : 0;

  const orgTotal = orgMetrics.uniqueResources || 0;
  const orgPassed = orgMetrics.passedResources || 0;
  const orgFailed = orgMetrics.failedResources || 0;
  const orgPassedPercent = orgTotal > 0 ? (orgPassed / orgTotal) * 100 : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        overflow: "hidden",
        ...CONFIG_CARD_SX,
        ...ACCOUNT_DETAIL_FONT_SX,
      }}
    >
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        {/*
          Shared-row grid keeps Organization score height = Account (score + checks),
          and aligns Resources / Failed by Severity across both columns.
          Trend spans the metric rows on desktop.
        */}
        <Box
          sx={{
            display: "grid",
            width: "100%",
            alignItems: "stretch",
            columnGap: isMobile ? 0 : 1.5,
            rowGap: 1.25,
            gridTemplateColumns: isMobile
              ? "1fr"
              : "minmax(150px, 20%) minmax(260px, 300px) minmax(0, 1fr)",
            gridTemplateRows: isMobile
              ? "auto"
              : "auto auto auto auto",
            gridTemplateAreas: isMobile
              ? `
                "orgTitle"
                "orgScore"
                "orgRes"
                "orgSev"
                "accTitle"
                "accTop"
                "accRes"
                "accSev"
                "trendTitle"
                "trend"
              `
              : `
                "orgTitle accTitle trendTitle"
                "orgScore accTop trend"
                "orgRes accRes trend"
                "orgSev accSev trend"
              `,
          }}
        >
          {/* Titles */}
          <Box
            sx={{
              gridArea: "orgTitle",
              display: "flex",
              alignItems: "center",
              minHeight: 28,
              pr: isMobile ? 0 : 1.5,
              borderRight: isMobile ? "none" : "1px solid #e5e7eb",
            }}
          >
            <Typography variant="subtitle2" sx={SECTION_TITLE_SX}>
              Organization View
            </Typography>
          </Box>

          <Box
            sx={{
              gridArea: "accTitle",
              display: "flex",
              alignItems: "center",
              minHeight: 28,
              mt: isMobile ? 1 : 0,
              pr: isMobile ? 0 : 1.5,
              borderRight: isMobile ? "none" : "1px solid #e5e7eb",
            }}
          >
            <Typography variant="subtitle2" sx={SECTION_TITLE_SX}>
              Account View
            </Typography>
          </Box>

          <Box
            sx={{
              gridArea: "trendTitle",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              minHeight: 28,
              mt: isMobile ? 1 : 0,
            }}
          >
            <Typography variant="subtitle2" sx={SECTION_TITLE_SX}>
              Security Score Trend (30 Days)
            </Typography>
            {scanObj?.runId && (
              <Button
                variant="contained"
                size="small"
                onClick={handleDownloadReport}
                disabled={isDownloadingCspmReport}
                startIcon={
                  isDownloadingCspmReport ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <DownloadIcon sx={{ fontSize: 16 }} />
                  )
                }
                sx={{
                  height: 28,
                  px: 1.25,
                  py: 0,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  lineHeight: 1.2,
                  boxShadow: "none",
                  flexShrink: 0,
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#1565c0",
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#90caf9",
                    color: "#fff",
                  },
                  "& .MuiButton-startIcon": {
                    marginRight: 0.5,
                  },
                }}
              >
                Download Report
              </Button>
            )}
          </Box>

          {/* Organization score — fills shared top row with Account score + checks */}
          <Box
            sx={{
              gridArea: "orgScore",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: isMobile ? 180 : 0,
              pr: isMobile ? 0 : 1.5,
              borderRight: isMobile ? "none" : "1px solid #e5e7eb",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 1.5,
                flex: 1,
                minHeight: 0,
                ...INNER_PANEL_SX,
              }}
            >
              {orgLoading ? (
                <CircularProgress size={32} />
              ) : (
                <>
                  <Box sx={{ position: "relative", width: 140, height: 140, mb: 0.75 }}>
                    <svg width="140" height="140">
                      <circle cx="70" cy="70" r="60" stroke="#e2e8f0" strokeWidth="9" fill="none" />
                    </svg>
                    <svg width="140" height="140" style={{ position: "absolute", top: 0, left: 0 }}>
                      <circle
                        cx="70"
                        cy="70"
                        r="60"
                        stroke="#0244a6"
                        strokeWidth="9"
                        fill="none"
                        strokeDasharray={2 * Math.PI * 60}
                        strokeDashoffset={2 * Math.PI * 60 * (1 - (orgMetrics.percentChecks / 100))}
                        style={{
                          transition: "stroke-dashoffset 0.6s",
                          transform: "rotate(-90deg)",
                          transformOrigin: "50% 50%",
                        }}
                      />
                    </svg>
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                        px: 1,
                      }}
                    >
                      <CloudProviderMark
                        sx={{
                          mr: 0,
                          mb: 0.25,
                          "& .cloud-provider-mark--aws": { height: 16, width: 28 },
                          "& .cloud-provider-mark--azure": { height: 20, width: 20 },
                        }}
                      />
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          fontSize: "1.35rem",
                          lineHeight: 1.1,
                          color: "#111827",
                          textAlign: "center",
                        }}
                      >
                        {formatPercent(orgMetrics.percentChecks)}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 500 }}>
                    Security Score
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {/* Account top: score + checks (same grid row as org score) */}
          <Box
            sx={{
              gridArea: "accTop",
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              height: "100%",
              minWidth: 0,
              pr: isMobile ? 0 : 1.5,
              borderRight: isMobile ? "none" : "1px solid #e5e7eb",
              boxSizing: "border-box",
            }}
          >
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, minHeight: 160 }}>
                <CircularProgress size={32} />
              </Box>
            ) : scanObj ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 1.25,
                    ...INNER_PANEL_SX,
                    flex: "0 0 auto",
                  }}
                >
                  <Box sx={{ position: "relative", width: 90, height: 90, mb: 0.5 }}>
                    <svg width="90" height="90">
                      <circle cx="45" cy="45" r="38" stroke="#e2e8f0" strokeWidth="6" fill="none" />
                    </svg>
                    <svg width="90" height="90" style={{ position: "absolute", top: 0, left: 0 }}>
                      <circle
                        cx="45"
                        cy="45"
                        r="38"
                        stroke="#3b82f6"
                        strokeWidth="6"
                        fill="none"
                        strokeDasharray={2 * Math.PI * 38}
                        strokeDashoffset={2 * Math.PI * 38 * (1 - (accountMetrics.percentChecks / 100))}
                        style={{
                          transition: "stroke-dashoffset 0.6s",
                          transform: "rotate(-90deg)",
                          transformOrigin: "50% 50%",
                        }}
                      />
                    </svg>
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                        px: 0.75,
                      }}
                    >
                      <CloudProviderMark
                        sx={{
                          mr: 0,
                          mb: 0.2,
                          "& .cloud-provider-mark--aws": { height: 12, width: 22 },
                          "& .cloud-provider-mark--azure": { height: 16, width: 16 },
                        }}
                      />
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          fontSize: "1rem",
                          lineHeight: 1.1,
                          color: "#111827",
                          textAlign: "center",
                        }}
                      >
                        {formatPercent(accountMetrics.percentChecks)}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", fontWeight: 500 }}>
                    Security Score
                  </Typography>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0.75, flex: "1 1 auto" }}>
                  <Box sx={{ textAlign: "center", p: 1, ...INNER_PANEL_SX }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", display: "block", mb: 0.4, fontWeight: 500 }}>
                      Total Checks
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827" }}>
                      {accountMetrics.executedChecks}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center", p: 1, borderRadius: "12px", bgcolor: "#f0fdf4", border: "1px solid #c6f6d5" }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", display: "block", mb: 0.4, fontWeight: 500 }}>
                      Passed
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#10b981" }}>
                      {accountMetrics.passedChecks}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center", p: 1, borderRadius: "12px", bgcolor: "#fef2f2", border: "1px solid #fecaca" }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", display: "block", mb: 0.4, fontWeight: 500 }}>
                      Failed
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#ef4444" }}>
                      {accountMetrics.failedChecks}
                    </Typography>
                  </Box>
                </Box>
              </>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, ...INNER_PANEL_SX, p: 2 }}>
                <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.875rem", fontWeight: 500, textAlign: "center" }}>
                  No account data available
                </Typography>
              </Box>
            )}
          </Box>

          {/* Resources — shared row */}
          <Box
            sx={{
              gridArea: "orgRes",
              pr: isMobile ? 0 : 1.5,
              borderRight: isMobile ? "none" : "1px solid #e5e7eb",
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ p: 1, ...INNER_PANEL_SX, height: "100%", boxSizing: "border-box" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.6 }}>
                <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", fontWeight: 600 }}>
                  Resources Scanned
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#111827" }}>
                  {orgLoading ? "—" : orgTotal}
                </Typography>
              </Box>
              <Box sx={{ position: "relative", height: 6, width: "100%", borderRadius: 3, overflow: "hidden", bgcolor: "#fee2e2", mb: 0.6 }}>
                <Box
                  sx={{
                    width: `${orgLoading ? 0 : orgPassedPercent}%`,
                    height: "100%",
                    bgcolor: "#10b981",
                    transition: "width 0.5s",
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" sx={{ color: "#10b981", fontSize: "0.7rem", fontWeight: 600 }}>
                  {orgLoading ? "—" : `${orgPassed} Passed`}
                </Typography>
                <Typography variant="caption" sx={{ color: "#ef4444", fontSize: "0.7rem", fontWeight: 600 }}>
                  {orgLoading ? "—" : `${orgFailed} Failed`}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              gridArea: "accRes",
              pr: isMobile ? 0 : 1.5,
              borderRight: isMobile ? "none" : "1px solid #e5e7eb",
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ p: 1, ...INNER_PANEL_SX, height: "100%", boxSizing: "border-box" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.6 }}>
                <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", fontWeight: 600 }}>
                  Resources Scanned
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#111827" }}>
                  {isLoading || !scanObj ? "—" : accountTotal}
                </Typography>
              </Box>
              <Box sx={{ position: "relative", height: 6, width: "100%", borderRadius: 3, overflow: "hidden", bgcolor: "#fee2e2", mb: 0.6 }}>
                <Box
                  sx={{
                    width: `${isLoading || !scanObj ? 0 : accountPassedPercent}%`,
                    height: "100%",
                    bgcolor: "#10b981",
                    transition: "width 0.5s",
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" sx={{ color: "#10b981", fontSize: "0.7rem", fontWeight: 600 }}>
                  {isLoading || !scanObj ? "—" : `${accountPassed} Passed`}
                </Typography>
                <Typography variant="caption" sx={{ color: "#ef4444", fontSize: "0.7rem", fontWeight: 600 }}>
                  {isLoading || !scanObj ? "—" : `${accountFailed} Failed`}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Failed by Severity — shared row */}
          <Box
            sx={{
              gridArea: "orgSev",
              pr: isMobile ? 0 : 1.5,
              borderRight: isMobile ? "none" : "1px solid #e5e7eb",
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ p: 1, ...INNER_PANEL_SX, height: "100%", boxSizing: "border-box" }}>
              <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", mb: 0.6, fontWeight: 600, display: "block" }}>
                Failed by Severity
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {!orgLoading &&
                  SEVERITY_LABELS.map((sev) => {
                    const count = orgSeverityData[sev] || 0;
                    if (count === 0) return null;
                    return (
                      <Chip
                        key={sev}
                        label={`${sev.charAt(0)}: ${count}`}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          backgroundColor: SEVERITY_BG_COLORS[sev] || "#f3f4f6",
                          color: SEVERITY_COLORS[sev] || "#6b7280",
                          borderRadius: "11px",
                        }}
                      />
                    );
                  })}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              gridArea: "accSev",
              pr: isMobile ? 0 : 1.5,
              borderRight: isMobile ? "none" : "1px solid #e5e7eb",
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ p: 1, ...INNER_PANEL_SX, height: "100%", boxSizing: "border-box" }}>
              <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", mb: 0.6, fontWeight: 600, display: "block" }}>
                Failed by Severity
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {!isLoading &&
                  scanObj &&
                  SEVERITY_LABELS.map((sev) => {
                    const count = accountSeverityData[sev] || 0;
                    if (count === 0) return null;
                    return (
                      <Chip
                        key={sev}
                        label={`${sev.charAt(0)}: ${count}`}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          backgroundColor: SEVERITY_BG_COLORS[sev] || "#f3f4f6",
                          color: SEVERITY_COLORS[sev] || "#6b7280",
                          borderRadius: "11px",
                        }}
                      />
                    );
                  })}
              </Box>
            </Box>
          </Box>

          {/* Trend chart card — title + download sit outside in trendTitle */}
          <Box
            sx={{
              gridArea: "trend",
              minWidth: 0,
              minHeight: isMobile ? 220 : 0,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                ...INNER_PANEL_SX,
                flex: 1,
                minHeight: 0,
                p: 1.25,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {trendChartData && trendChartData.labels && trendChartData.labels.length > 0 ? (
                <Box sx={{ flex: 1, minHeight: 0, width: "100%", overflow: "hidden" }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      overflow: "hidden",
                      transition: "box-shadow 0.2s",
                      borderRadius: 2,
                      "&:hover": {
                        boxShadow: "0 4px 16px 0 rgba(25, 118, 210, 0.10)",
                      },
                      position: "relative",
                    }}
                  >
                    <Line ref={chartRef} data={chartData} options={chartOptions} />
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, minHeight: 160 }}>
                  <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.75rem" }}>
                    No trend data available
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      
      {/* Snackbar for errors */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default CSPMHeader; 